(ns frontend.ui.page.lp-report-editor.event
  (:require
   [re-frame.core :as rf]
   [frontend.controller.websocket.event :as ws]
   [potpuri.core :as p]
   [clojure.data :as data]
   [frontend.routing :as routing]
   [frontend.ui.page.lp-report-editor.sub :as sub]
   [common.util :as cu]))

(defn- report-images->by-id
  [report-images]
  (cond
    (map? report-images)
    report-images

    (sequential? report-images)
    (->> report-images
         (keep (fn [image]
                 (when-let [image-id (:db/id image)]
                   [image-id image])))
         (into {}))

    :else
    {}))

(rf/reg-event-fx
 ::on-init-data
 (fn [{:keys [db]} [_ {:keys [body]}]]
   (let [report (:lp-report body)]
     {:db (-> db
              (assoc-in sub/report-path report)
              (assoc-in sub/tenant-path (:tenant body))
              (assoc-in sub/fund-path (:fund body))
              (assoc-in sub/reporting-admin?-path (:reporting-admin? body))
              (assoc-in sub/account-id-path (:account-id body))
              (assoc-in sub/team-memberships-ids-path (:account-team-memberships body))
              (assoc-in sub/attachments-path (:lp-report-attachments body))
              (assoc-in sub/holdings-path (:lp-report-holdings body))
              (assoc-in sub/report-images-path (report-images->by-id (:report-images body)))
              (assoc-in sub/investor-contacts-path (:lp-report-investor-contacts body))
              (assoc-in sub/investor-commitments-path (:lp-report-investor-commitments body))
              (assoc-in sub/feeder-investor-contacts-path
                        (:lp-report-feeder-investor-contacts body))
              (assoc-in sub/feeder-investor-commitments-path (:lp-report-feeder-investor-commitments body))
              (assoc-in sub/feeder-funds-path (:lp-report-feeder-funds body))
              (assoc-in sub/feeder-reports-path (:lp-report-feeder-reports body))
              (assoc-in sub/accounts-path (:accounts body))
              (assoc-in sub/active-step-path
                        (get report :lp-report/step :lp-report.step/initialization))
              (assoc-in sub/initialized-path true))
      :fx (cond-> [[:dispatch [::load-import-history (:db/id report)]]]
             (or (cu/blank-value? (:lp-report/title report))
                 (cu/blank-value? (:lp-report/template-type report))
                 (cu/blank-value? (:lp-report/final-approver report))
                 (cu/blank-value? (:lp-report/text-approver report))
                 (cu/blank-value? (:lp-report/data-approver report))
                 (cu/blank-value? (:lp-report/process-owners report))
                 (cu/blank-value? (:lp-report/text-contributors report))
                 (cu/blank-value? (:lp-report/data-contributors report)))
             (conj [:dispatch
                    [:frontend.ui.page.lp-report-editor.step-1-initialization.event/start-basic-edit]]))})))

(rf/reg-event-fx
 ::on-init-data-failed
 (fn [{:keys [db]} [_ _args]]
   (js/console.log "Failed to load initial reporting data")
   {:db (assoc db :report/error? true)}))

(rf/reg-event-fx
 ::on-data-reloaded
 (fn [{:keys [db]} [_ on-success {:keys [body]}]]
   {:db (-> db
            (assoc-in sub/report-path (:lp-report body))
            (assoc-in sub/report-images-path (report-images->by-id (:report-images body)))
            (assoc-in sub/fund-path (:fund body))
            (assoc-in sub/reporting-admin?-path (:reporting-admin? body))
            (assoc-in sub/account-id-path (:account-id body))
            (assoc-in sub/team-memberships-ids-path (:account-team-memberships body))
            (assoc-in sub/attachments-path (:lp-report-attachments body))
            (assoc-in sub/holdings-path (:lp-report-holdings body))
            (assoc-in sub/investor-contacts-path (:lp-report-investor-contacts body))
            (assoc-in sub/investor-commitments-path (:lp-report-investor-commitments body))
            (assoc-in sub/feeder-investor-contacts-path (:lp-report-feeder-investor-contacts body))
            (assoc-in sub/feeder-investor-commitments-path (:lp-report-feeder-investor-commitments body))
            (assoc-in sub/feeder-funds-path (:lp-report-feeder-funds body))
            (assoc-in sub/feeder-reports-path (:lp-report-feeder-reports body))
            (assoc-in sub/accounts-path (:accounts body)))
    ;; Refresh alongside the report so a finished import appears in the list -
    ;; but not while one is running, because the progress poll routes through
    ;; here every 200ms and the history cannot change until it ends.
    :fx (cond-> []
          (not= :processing.status/processing
                (:processing-status (sub/parse-data-status
                                     (:lp-report/report-data-status (:lp-report body)))))
          (conj [:dispatch [::load-import-history (:db/id (:lp-report body))]])

          on-success
          (conj [:dispatch on-success]))}))


(rf/reg-event-fx
 ::on-reload-data-failed
 (fn [{:keys [db]} [_ _args]]
   (js/console.log "Failed to reload reporting data")
   {:db (assoc db :report/error? true)}))

(rf/reg-event-fx
 ::re-load-editor-data
 (fn [{db :db} [_ report-id on-success]]
   {:db       (assoc db sub/reloading-path true)
    ::ws/send {:id         :lp-report/load-report-entities
               :body       {:report-id report-id}
               :on-success [::on-data-reloaded on-success]
               :on-error   [::on-reload-data-failed]}}))

(def ^:private import-in-progress-statuses
  "Statuses meaning the import has not finished yet."
  #{:processing.status/processing
    :status/uploading})

(rf/reg-event-fx
 ::reload-fund-data-progress
 (fn [{db :db} [_ report-id]]
   (let [report (get-in db sub/report-path)
         data-status (-> report
                         :lp-report/report-data-status
                         (sub/parse-data-status))]
     (when (contains? import-in-progress-statuses
                      (:processing-status data-status))
       {:fx [[:dispatch-later
              [{:ms       200
                :dispatch
                [::re-load-editor-data
                 report-id
                 [::reload-fund-data-progress report-id]]}]]]}))))

(rf/reg-event-fx
 ::process-fund-report-file
 (fn [{_db :db} [_ report file-id]]
   (let [report-id (:db/id report)
         template-type (:lp-report/template-type report)
         process-fund-and-holdings? (or (= template-type :lp-report-template.lifeline/fund)
                                        (= template-type :lp-report-template.lifeline/fund-v)
                                        (= template-type :lp-report-template.lifeline/fund-vi)
                                        (= template-type :lp-report-template.innovestor/technology-fund-v2))] ;;TODO refactor when more templates use single excel import
     {;; Import is synchronous, so :on-success fires only when it is done.
      ;; Poll alongside the request instead.
      :fx [[:dispatch [::reload-fund-data-progress report-id]]]
      ::ws/send {:id         (if process-fund-and-holdings?
                               :lp-report/process-fund-and-holdings-data
                               :lp-report/process-fund-data)
                 :body       {:report-id     report-id
                              :attachment-id file-id}
                 :on-success [::re-load-editor-data
                              report-id
                              nil]
                 :on-error   [::lp-fund-report-data-file-update-failed]}})))

(rf/reg-event-fx
 ::commitment-material-upload-failed
 (fn [{_db :db} [_ on-error error]]
   (js/console.log "Failed to add commitment material " error)
   (when on-error
     {:fx [[:dispatch (conj on-error error)]]})))

(rf/reg-event-fx
 ::process-commitment-material-upload
 (fn [{_db :db} [_ master-report-id commitment-id file-id]]
   {::ws/send {:id         :lp-report/add-private-material-file
               :body       {:commitment-id commitment-id
                            :attachment-id  file-id}
               :on-success [::re-load-editor-data
                            master-report-id
                            [::reload-fund-data-progress master-report-id]]
               :on-error   [::commitment-material-upload-failed]}}))

(rf/reg-event-fx
 ::process-fund-report-material-file
 (fn [{_db :db} [_ master-report-id report-id file-id]]
   {::ws/send {:id         :lp-report/add-material-file
               :body       {:report-id     report-id
                            :attachment-id file-id}
               :on-success [::re-load-editor-data
                            master-report-id
                            [::reload-fund-data-progress master-report-id]]
               :on-error   [::lp-fund-report-data-file-update-failed]}}))

(rf/reg-event-fx
 ::update-lp-report
 (fn [_ [_ report ui-report on-success]]
   (let [[old-values new-values _unchanged] (data/diff report ui-report)
         updates {:data  (merge (->> old-values
                                     (map (fn [[k _]]
                                            ;; retract old values that are not overwritten
                                            [k nil]))
                                     (into {}))
                                new-values)
                  :db/id (:db/id report)}]
     {::ws/send {:id         :entity/update-attributes
                 :body       updates
                 :on-success [::lp-report-updated (:db/id report) on-success]
                 :on-error   [::lp-report-update-failed]}})))

(rf/reg-event-fx
 ::activate-step
 (fn [{db :db} [_ step]]
   {:db (assoc-in db sub/active-step-path step)}))

(rf/reg-event-fx
 ::activate-report-step
 (fn [{db :db} [_ _report-id]]
   (let [report (get-in db sub/report-path)]
     {:db (assoc-in db sub/active-step-path (:lp-report/step report))})))

(rf/reg-event-fx
 ::load-initialization-data
 (fn [{db :db} [_ report-id]]
   {:db       (assoc db sub/reloading-path true)
    ::ws/send {:id         :lp-report/load-report-entities
               :body       {:report-id report-id}
               :on-success [::on-init-data]
               :on-error   [::on-init-data-failed]}}))

(rf/reg-event-fx
 ::reset-editor
 (fn [{db :db} [_ report-id fund-id]]
   (when-not (and (get-in db sub/initialized-path)
                  (= fund-id (get-in db sub/close-to-fund-path))
                  (= report-id (get-in db (conj sub/report-path :db/id))))
     {:db (-> db
              (assoc-in sub/initialized-path false)
              (assoc-in sub/close-to-fund-path fund-id))
      :fx [[:dispatch [::load-initialization-data report-id]]]})))

(rf/reg-event-fx
 ::load-import-history-ok
 (fn [{db :db} [_ resp]]
   {:db (assoc-in db sub/import-history-path (-> resp :body :import-history))}))

(rf/reg-event-fx
 ::load-import-history-failed
 (fn [{_db :db} [_ err]]
   (js/console.log "Loading import history failed : " err)))

(rf/reg-event-fx
 ::load-import-history
 (fn [_ [_ report-id]]
   {::ws/send {:id         :lp-report/import-history
               :body       {:report-id report-id}
               :on-success [::load-import-history-ok]
               :on-error   [::load-import-history-failed]}}))

(rf/reg-event-fx
 ::download-data-file-ok
 (fn [{_db :db} [_ resp]]
   (.open js/window
          (-> resp
              :body
              :download-url)
          "_blank")
   nil))

(rf/reg-event-fx
 ::download-data-file-failed
 (fn [{_db :db} [_ err]]
   (js/console.log "Download data file failed : " err)))

(rf/reg-event-fx
 ::download-data-file
 (fn [_ [_ report-id file-id]]
   {::ws/send {:id         :lp-report/download-data-file
               :body       {:report-id report-id
                            :file-id   file-id}
               :on-success [::download-data-file-ok]
               :on-error   [::download-data-file-failed]}}))

(rf/reg-event-fx
 ::close-editor
 (fn [{db :db} _]
   (let [open-fund-id (get-in db sub/close-to-fund-path)]
     {:db (p/dissoc-in db sub/editor-path)
      ::routing/old-routing {:path-args [:entity.type/fund :id open-fund-id]}})))

(rf/reg-event-fx
 ::open-burned-pdf-ok
 (fn [{:keys [_db]} [_ resp]]
   (let [body (:body resp)
         url (:download-url body)
         w (when url
             (.open js/window "about:blank" "_blank"))]
     (when w
       (aset w "location" (:download-url body))))
   {}))

(rf/reg-event-fx
 ::open-burned-pdf-failed
 (fn [{:keys [_db]} [_ _args]]
   (js/console.log "Failed to load burned pdf")
   {}))

(rf/reg-event-fx
 ::open-burned-pdf
 (fn [{_db :db} [_ burned-pdfs]]
   {::ws/send {:id         :attachment/force-download-url
               :body       {:attachment-id (first burned-pdfs)}
               :on-success [::open-burned-pdf-ok]
               :on-error   [::open-burned-pdf-failed]}}))

(rf/reg-event-fx
 ::lp-fund-report-data-file-update-failed
 (fn [{_db :db} [_ on-error error]]
   (js/console.log "Failed to update fund report data file ref " error)
   (when on-error
     {:fx [[:dispatch (conj on-error error)]]})))

(rf/reg-event-fx
 ::lp-report-updated
 (fn [{_db :db} [_ report-id on-success _resp]]
   {:fx [[:dispatch [::re-load-editor-data report-id on-success]]]}))

(rf/reg-event-fx
 ::lp-report-update-failed
 (fn [{_db :db} [_ _result-key {:keys [_data _errors]}]]
   (js/console.log "LP Report update failed")))
